import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Check, MapPin, ArrowLeft } from 'lucide-react'
import { useGooglePlaces } from '@/hooks/useGooglePlaces'
import {
  lookupJobSitesByAddressV2,
  getJobSiteDetailsV2,
  updateJobSiteV2,
  createJobSiteForProjectV2,
  assignJobSiteToProjectV2,
  updateClientBillingAddressV2,
  type JobSiteLookupMatchV2,
  type JobSiteAddressV2,
} from '@/lib/api/v2/job-sites'
import { showErrorToast, showSuccessToast } from '@/lib/toast'

const addressSchema = z.object({
  postalCode: z.string().min(4, 'Code postal requis'),
  city: z.string().min(1, 'Ville requise'),
  street: z.string().min(1, 'Rue requise'),
  number: z.string().min(1, 'Numéro requis'),
  box: z.string().optional(),
  floor: z.string().optional(),
})

const updateSchema = addressSchema.extend({
  label: z.string().optional(),
})

type AddressFormData = z.infer<typeof addressSchema>
type UpdateFormData = z.infer<typeof updateSchema>

interface LocationState {
  customerId?: string
  hasBillingAddress?: boolean
  from?: string
}

export default function JobSiteEditPage() {
  const { jobSiteId, projectId } = useParams<{ jobSiteId?: string; projectId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | undefined

  const isUpdateMode = !!jobSiteId
  const isCreateMode = !!projectId && !jobSiteId

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(isUpdateMode)
  const [addressAutoCompleted, setAddressAutoCompleted] = useState(false)

  // Duplicate detection state (create mode only)
  const [showReuseDialog, setShowReuseDialog] = useState(false)
  const [jobSiteMatches, setJobSiteMatches] = useState<JobSiteLookupMatchV2[]>([])
  const [pendingAddress, setPendingAddress] = useState<AddressFormData | null>(null)
  const [selection, setSelection] = useState<
    | { type: 'new' }
    | { type: 'existing'; jobSiteId: string }
    | null
  >(null)

  // Billing address sync state (create mode only)
  const [showBillingDialog, setShowBillingDialog] = useState(false)
  const [createdJobSiteAddress, setCreatedJobSiteAddress] = useState<JobSiteAddressV2 | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<UpdateFormData>({
    resolver: zodResolver(isUpdateMode ? updateSchema : addressSchema),
    mode: 'onChange',
    defaultValues: {
      postalCode: '',
      city: '',
      street: '',
      number: '',
      box: '',
      floor: '',
      label: '',
    },
  })

  // Load existing job site data in update mode
  useEffect(() => {
    if (!isUpdateMode || !jobSiteId) return

    async function loadJobSite() {
      try {
        const jobSite = await getJobSiteDetailsV2(jobSiteId!)
        setValue('street', jobSite.address.street)
        setValue('number', jobSite.address.number)
        setValue('postalCode', jobSite.address.postalCode)
        setValue('city', jobSite.address.city)
        setValue('box', jobSite.address.box || '')
        setValue('floor', jobSite.address.floor || '')
        setValue('label', jobSite.label || '')
      } catch (error) {
        console.error('Error loading job site:', error)
        showErrorToast('Erreur lors du chargement du chantier')
        navigate(-1)
      } finally {
        setIsLoading(false)
      }
    }

    loadJobSite()
  }, [jobSiteId, isUpdateMode, navigate, setValue])

  // Google Places Autocomplete (create mode only)
  const { containerRef, isLoaded, error } = useGooglePlaces((place) => {
    setValue('street', place.street, { shouldValidate: true })
    setValue('number', place.number, { shouldValidate: true })
    setValue('postalCode', place.postalCode, { shouldValidate: true })
    setValue('city', place.city, { shouldValidate: true })

    if (place.box) {
      setValue('box', place.box)
    }

    setAddressAutoCompleted(true)
    setTimeout(() => setAddressAutoCompleted(false), 3000)
  })

  const formattedAddress = useMemo(() => {
    const a = pendingAddress || getValues()
    const line1 = `${a.street || ''} ${a.number || ''}`.trim()
    const box = a.box ? ` bte ${a.box}` : ''
    const line2 = `${a.postalCode || ''} ${a.city || ''}`.trim()
    return `${line1}${box}${line1 && line2 ? ', ' : ''}${line2}`.trim()
  }, [getValues, pendingAddress])

  const billingFormattedAddress = useMemo(() => {
    if (!createdJobSiteAddress) return ''
    const line1 = `${createdJobSiteAddress.street || ''} ${createdJobSiteAddress.number || ''}`.trim()
    const box = createdJobSiteAddress.box ? ` bte ${createdJobSiteAddress.box}` : ''
    const floor = createdJobSiteAddress.floor ? `, ${createdJobSiteAddress.floor}` : ''
    const line2 = `${createdJobSiteAddress.postalCode || ''} ${createdJobSiteAddress.city || ''}`.trim()
    return `${line1}${box}${floor}${line1 && line2 ? ', ' : ''}${line2}`.trim()
  }, [createdJobSiteAddress])

  const handleDialogOpenChange = (open: boolean) => {
    setShowReuseDialog(open)
    if (!open) {
      setJobSiteMatches([])
      setPendingAddress(null)
      setSelection(null)
    }
  }

  const handleBillingDialogOpenChange = (open: boolean) => {
    setShowBillingDialog(open)
    if (!open) {
      setCreatedJobSiteAddress(null)
    }
  }

  const handleContinueWithSelection = async () => {
    if (!projectId) {
      showErrorToast('Erreur: ID de projet manquant')
      return
    }
    if (!pendingAddress) {
      showErrorToast('Erreur: adresse manquante')
      return
    }
    if (!selection) {
      showErrorToast('Veuillez sélectionner une option')
      return
    }

    setIsSaving(true)
    try {
      if (selection.type === 'existing') {
        // Assign existing job site to project
        await assignJobSiteToProjectV2(projectId, { jobSiteId: selection.jobSiteId })
        showSuccessToast('Chantier assigné au projet')
        navigateBack()
      } else {
        // Create new job site
        await createJobSiteForProjectV2(projectId, {
          address: pendingAddress,
        })

        // Check if we should prompt for billing address sync
        if (state?.customerId && state?.hasBillingAddress === false) {
          setCreatedJobSiteAddress(pendingAddress)
          handleDialogOpenChange(false)
          setShowBillingDialog(true)
        } else {
          showSuccessToast('Chantier créé')
          navigateBack()
        }
      }
    } catch (e) {
      console.error('Error creating/assigning job site:', e)
      showErrorToast('Erreur lors de la création/assignation du chantier')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSyncBillingAddress = async (sync: boolean) => {
    if (sync && state?.customerId && createdJobSiteAddress) {
      try {
        await updateClientBillingAddressV2(state.customerId, {
          address: createdJobSiteAddress,
        })
        showSuccessToast('Adresse de facturation mise à jour')
      } catch (error) {
        console.error('Error updating billing address:', error)
        showErrorToast('Erreur lors de la mise à jour de l\'adresse de facturation')
      }
    }
    handleBillingDialogOpenChange(false)
    navigateBack()
  }

  const navigateBack = () => {
    if (state?.from) {
      navigate(state.from, { replace: true })
    } else {
      navigate(-1)
    }
  }

  const onSubmit = async (data: UpdateFormData) => {
    if (isUpdateMode && jobSiteId) {
      // Update mode: direct update without duplicate check
      setIsSaving(true)
      try {
        await updateJobSiteV2(jobSiteId, {
          address: {
            street: data.street,
            number: data.number,
            postalCode: data.postalCode,
            city: data.city,
            box: data.box || undefined,
            floor: data.floor || undefined,
            country: 'BE',
          },
          label: data.label || undefined,
        })
        showSuccessToast('Chantier mis à jour')
        navigateBack()
      } catch (error) {
        console.error('Error updating job site:', error)
        showErrorToast('Erreur lors de la mise à jour du chantier')
      } finally {
        setIsSaving(false)
      }
    } else if (isCreateMode && projectId) {
      // Create mode: check for duplicates first
      setIsSaving(true)
      try {
        const lookup = await lookupJobSitesByAddressV2({
          street: data.street,
          number: data.number,
          postalCode: data.postalCode,
          city: data.city,
          box: data.box || undefined,
          floor: data.floor || undefined,
          country: 'BE',
        })

        const matches = lookup.matches || []

        if (matches.length === 0) {
          // No duplicates: create directly
          await createJobSiteForProjectV2(projectId, {
            address: {
              street: data.street,
              number: data.number,
              postalCode: data.postalCode,
              city: data.city,
              box: data.box || undefined,
              floor: data.floor || undefined,
              country: 'BE',
            },
          })

          // Check if we should prompt for billing address sync
          if (state?.customerId && state?.hasBillingAddress === false) {
            setCreatedJobSiteAddress({
              street: data.street,
              number: data.number,
              postalCode: data.postalCode,
              city: data.city,
              box: data.box || undefined,
              floor: data.floor || undefined,
              country: 'BE',
            })
            setShowBillingDialog(true)
          } else {
            showSuccessToast('Chantier créé')
            navigateBack()
          }
          return
        }

        // Matches found: let user decide
        setPendingAddress(data)
        setJobSiteMatches(matches)
        setSelection(
          matches.length === 1
            ? { type: 'existing', jobSiteId: matches[0].id }
            : { type: 'new' }
        )
        setShowReuseDialog(true)
      } catch (error) {
        console.error('Error checking existing job sites:', error)
        showErrorToast('Erreur lors de la recherche de chantiers existants')
      } finally {
        setIsSaving(false)
      }
    }
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto">
          <Card className="p-6">
            <p className="text-center text-muted-foreground">Chargement...</p>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={navigateBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>

        <Card className="p-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">
                {isUpdateMode ? 'Modifier le chantier' : 'Nouveau chantier'}
              </h1>
              <p className="text-muted-foreground">
                {isUpdateMode
                  ? 'Modifiez l\'adresse et le nom du chantier'
                  : 'Créez un nouveau chantier pour ce projet'}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Google Places Autocomplete - CREATE MODE ONLY */}
              {isCreateMode && (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Rechercher une adresse *</Label>
                      {addressAutoCompleted && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <Check className="h-4 w-4" />
                          Adresse complétée
                        </span>
                      )}
                    </div>
                    <div ref={containerRef} className="w-full" style={{ minHeight: '40px' }} />
                    {error && (
                      <p className="text-xs text-amber-600">
                        ⚠️ {error} - Remplissez manuellement ci-dessous
                      </p>
                    )}
                    {isLoaded && !error && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        Commencez à taper pour des suggestions (ex: Rue du Marché 140, Jodoigne)
                      </p>
                    )}
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Ou remplissez manuellement
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Street */}
              <div className="space-y-2">
                <Label htmlFor="street">Rue *</Label>
                <Input
                  id="street"
                  {...register('street')}
                  placeholder="Rue du Marché"
                  className={errors.street ? 'border-red-500' : ''}
                />
                {errors.street && (
                  <p className="text-sm text-red-600">{errors.street.message}</p>
                )}
              </div>

              {/* Number and Box */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number">Numéro *</Label>
                  <Input
                    id="number"
                    {...register('number')}
                    placeholder="140"
                    className={errors.number ? 'border-red-500' : ''}
                  />
                  {errors.number && (
                    <p className="text-sm text-red-600">{errors.number.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="box">Boîte</Label>
                  <Input id="box" {...register('box')} placeholder="A" />
                </div>
              </div>

              {/* Postal Code and City */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Code postal *</Label>
                  <Input
                    id="postalCode"
                    {...register('postalCode')}
                    placeholder="1370"
                    maxLength={4}
                    className={errors.postalCode ? 'border-red-500' : ''}
                  />
                  {errors.postalCode && (
                    <p className="text-sm text-red-600">{errors.postalCode.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">Localité *</Label>
                  <Input
                    id="city"
                    {...register('city')}
                    placeholder="Jodoigne"
                    className={errors.city ? 'border-red-500' : ''}
                  />
                  {errors.city && (
                    <p className="text-sm text-red-600">{errors.city.message}</p>
                  )}
                </div>
              </div>

              {/* Floor */}
              <div className="space-y-2">
                <Label htmlFor="floor">Étage / Précision</Label>
                <Input
                  id="floor"
                  {...register('floor')}
                  placeholder="Ex: Appt 3B, 2e étage, Rez-de-chaussée"
                />
              </div>

              {/* Label - UPDATE MODE ONLY */}
              {isUpdateMode && (
                <div className="space-y-2">
                  <Label htmlFor="label">Nom du chantier (optionnel)</Label>
                  <Input
                    id="label"
                    {...register('label')}
                    placeholder="Ex: Maison familiale"
                  />
                  <p className="text-xs text-muted-foreground">
                    Un nom pour identifier ce chantier
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={navigateBack} disabled={isSaving}>
                  Annuler
                </Button>
                <Button type="submit" disabled={!isValid || isSaving}>
                  {isSaving ? 'Enregistrement...' : isUpdateMode ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>

      {/* Duplicate Detection Dialog - CREATE MODE ONLY */}
      <Dialog open={showReuseDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-[650px]">
          <DialogHeader>
            <DialogTitle>Ce chantier existe déjà</DialogTitle>
            <DialogDescription>
              Soly a trouvé des chantiers enregistrés pour cette adresse. Vous pouvez les
              réutiliser ou créer un nouveau chantier.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p className="text-muted-foreground text-xs mb-1">Adresse saisie</p>
              <p className="font-medium">{formattedAddress || 'Adresse'}</p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold">Réutiliser un chantier existant</p>
              <div className="space-y-2">
                {jobSiteMatches.slice(0, 5).map((m) => {
                  const isSelected =
                    selection?.type === 'existing' && selection.jobSiteId === m.id
                  const label =
                    m.formattedAddress ||
                    `${m.address.street} ${m.address.number}, ${m.address.postalCode} ${m.address.city}`
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelection({ type: 'existing', jobSiteId: m.id })}
                      className={
                        `w-full text-left p-4 border-2 rounded-lg transition-all ` +
                        (isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary')
                      }
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-semibold">N° {m.reference}</p>
                          <p className="text-sm text-muted-foreground truncate">{label}</p>
                        </div>
                        {isSelected && (
                          <div className="mt-0.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setSelection({ type: 'new' })}
                className={
                  `w-full text-left p-4 border-2 rounded-lg transition-all ` +
                  (selection?.type === 'new'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary')
                }
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold">Créer un nouveau chantier</p>
                    <p className="text-sm text-muted-foreground">
                      Utiliser l'adresse que vous venez d'encoder
                    </p>
                  </div>
                  {selection?.type === 'new' && (
                    <div className="mt-0.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleContinueWithSelection}
              disabled={isSaving || !selection}
            >
              {isSaving ? 'Enregistrement...' : 'Continuer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Address Sync Dialog - CREATE MODE ONLY */}
      <Dialog open={showBillingDialog} onOpenChange={handleBillingDialogOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Adresse de facturation</DialogTitle>
            <DialogDescription>
              Voulez-vous utiliser la même adresse pour la facturation du client ?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p className="text-muted-foreground text-xs mb-1">Adresse du chantier</p>
              <p className="font-medium">{billingFormattedAddress || 'Adresse'}</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSyncBillingAddress(false)}
            >
              Non, laisser vide
            </Button>
            <Button type="button" onClick={() => handleSyncBillingAddress(true)}>
              Oui, utiliser cette adresse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
